import { useState, useMemo, useCallback } from "react";
import { SortDirection } from "../components/shared/TableComponents";

export type SortType = "auto" | "string" | "number" | "date";

export interface SortableColumn {
  sortKey: string | null;
  sortType?: SortType;
}

interface UseTableSortReturn<T> {
  sortedData: T[];
  sortKey: string | null;
  sortDirection: SortDirection;
  handleSort: (key: string) => void;
  getSortDirection: (key: string) => SortDirection;
}

/**
 * Parse a date value to a timestamp for comparison.
 *
 * Supported formats:
 *  - ISO 8601:    "2026-01-22T19:23:21.648448+00:00"
 *  - YYYY-MM-DD:  "2026-01-22"
 *  - MM/DD/YYYY:  "01/22/2026"
 *  - Empty string or invalid → returns null
 */
function parseDate(val: any): number | null {
  if (val == null || val === "") return null;

  const str = String(val).trim();

  // MM/DD/YYYY — convert to YYYY-MM-DD so Date.parse handles it reliably
  const slashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    const ts = Date.parse(
      `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
    );
    return isNaN(ts) ? null : ts;
  }

  // ISO 8601 or YYYY-MM-DD — Date.parse handles these natively
  const ts = Date.parse(str);
  return isNaN(ts) ? null : ts;
}

function resolveValue(obj: any, key: string): any {
  return key.includes(".")
    ? key.split(".").reduce((o, k) => o?.[k], obj)
    : obj[key];
}

/**
 * Reusable table sort hook.
 *
 * @param data    - The array of row objects to sort.
 * @param columns - Column definitions with sortKey and optional sortType.
 *                  When omitted, all columns use "auto" detection.
 *
 * Sort types:
 *  - "auto"   (default) — tries number first, then falls back to string.
 *  - "number" — forces numeric comparison.
 *  - "string" — forces case-insensitive string comparison.
 *  - "date"   — parses as date (ISO 8601, YYYY-MM-DD, MM/DD/YYYY).
 */
export function useTableSort<T = any>(
  data: T[],
  columns?: SortableColumn[],
): UseTableSortReturn<T> {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const columnMap = useMemo(() => {
    const map: Record<string, SortType> = {};
    if (columns) {
      columns.forEach((col) => {
        if (col.sortKey) {
          map[col.sortKey] = col.sortType || "auto";
        }
      });
    }
    return map;
  }, [columns]);

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDirection("asc");
      } else {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      }
    },
    [sortKey, sortDirection],
  );

  const getSortDirection = useCallback(
    (key: string): SortDirection => {
      return sortKey === key ? sortDirection : null;
    },
    [sortKey, sortDirection],
  );

  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection) return data;

    const sortType = columnMap[sortKey] || "auto";

    return [...data].sort((a: any, b: any) => {
      const aVal = resolveValue(a, sortKey);
      const bVal = resolveValue(b, sortKey);

      // Handle null/undefined/empty/whitespace/NaN — always push to bottom
      const isEmpty = (val: any) =>
        val == null ||
        (typeof val === "string" && val.trim() === "") ||
        (typeof val === "number" && isNaN(val));
      const aEmpty = isEmpty(aVal);
      const bEmpty = isEmpty(bVal);
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;

      // Date comparison
      if (sortType === "date") {
        const aDate = parseDate(aVal);
        const bDate = parseDate(bVal);
        if (aDate != null && bDate != null) {
          return sortDirection === "asc" ? aDate - bDate : bDate - aDate;
        }
        // If one fails to parse, push to bottom
        if (aDate == null) return 1;
        return -1;
      }

      // Numeric comparison
      if (sortType === "number") {
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      // String comparison
      if (sortType === "string") {
        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();
        if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
        if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
        return 0;
      }

      // Auto: try number first, fall back to string
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
      if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortKey, sortDirection, columnMap]);

  return { sortedData, sortKey, sortDirection, handleSort, getSortDirection };
}
